import { FinishedUnaryCall } from "@protobuf-ts/runtime-rpc";
import { useEffect, useMemo, useReducer, useRef } from "react";

import { transformObjectToString } from "../helpers/transformObjectToString";
import { useQueryAbortSignal } from "../context/QueryAbort/QueryAbortContext";

interface State<R> {
  data?: R;
  error?: Error;
  loading: boolean;
}

type Cache<T> = { [name: string]: T };

type Action<R> =
  | { type: "loading" }
  | { type: "fetched"; payload: R }
  | { type: "error"; payload: Error };

export interface GrpcQueryOptions {
  abort?: AbortSignal;
}

interface Props<V extends Object, R extends Object> {
  variables: V;
  method: (
    variables: V,
    options?: GrpcQueryOptions
  ) => Promise<FinishedUnaryCall<V, R>>;
  refetchOnWindowFocus?: boolean;
  refetchOnWindowFocusThreshold?: number;
}

interface Response<R> extends State<R> {
  refetch: () => void;
}

function useGrpcQuery<V extends Object, R extends Object>({
  variables,
  method,
  refetchOnWindowFocus = false,
  refetchOnWindowFocusThreshold = 0,
}: Props<V, R>): Response<R> {
  const abortSignal = useQueryAbortSignal();
  const cache = useRef<Cache<R>>({});
  const unMounted = useRef<boolean>(false);
  const nextRequest = useRef<boolean>(false);
  const lastBlurTime = useRef<number | null>(null);
  const name = useMemo(() => {
    const variablesName = transformObjectToString(variables);

    return `${method.name}:${variablesName}`;
  }, [variables]);

  const initialState: State<R> = {
    loading: false,
    error: undefined,
    data: undefined,
  };

  // Keep state logic separated

  const fetchReducer = (state: State<R>, action: Action<R>): State<R> => {
    switch (action.type) {
      case "loading":
        return { ...initialState, loading: true };
      case "fetched":
        return { ...initialState, data: action.payload };
      case "error":
        return { ...initialState, error: action.payload };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(fetchReducer, initialState);

  useEffect(() => {
    return () => {
      unMounted.current = true;
    };
  }, []);

  // Handle refetch on window focus with inactivity threshold
  useEffect(() => {
    if (!refetchOnWindowFocus) return;

    const handleBlur = () => {
      // Window loses focus - record the time
      lastBlurTime.current = Date.now();
    };

    const handleFocus = () => {
      // Window gains focus - check if we should refetch
      if (!unMounted.current && name) {
        let shouldRefetch = true;

        if (
          refetchOnWindowFocusThreshold > 0 &&
          lastBlurTime.current !== null
        ) {
          const inactiveDuration = Date.now() - lastBlurTime.current;
          shouldRefetch = inactiveDuration >= refetchOnWindowFocusThreshold;
        }

        if (shouldRefetch) {
          // Clear cache to force fresh data fetch
          cache.current = {};

          const fetchData = async () => {
            dispatch({ type: "loading" });

            try {
              const { response } = await method(variables, {
                abort: abortSignal,
              });
              cache.current[name] = response;

              if (!unMounted.current && !nextRequest.current) {
                dispatch({ type: "fetched", payload: response });
              }
            } catch (error: any) {
              if (abortSignal?.aborted) return;

              if (!unMounted.current && !nextRequest.current) {
                dispatch({ type: "error", payload: error as Error });
              }
            }
          };

          void fetchData();
        }

        // Reset blur time after handling focus
        lastBlurTime.current = null;
      }
    };

    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };
  }, [
    refetchOnWindowFocus,
    refetchOnWindowFocusThreshold,
    name,
    method,
    variables,
  ]);

  useEffect(() => {
    if (!name) return;

    const fetchData = async () => {
      dispatch({ type: "loading" });

      if (cache.current[name]) {
        dispatch({ type: "fetched", payload: cache.current[name] });
        return;
      }

      try {
        const { response } = await method(variables, { abort: abortSignal });

        cache.current[name] = response;

        if (unMounted.current) {
          return;
        }

        if (!nextRequest.current) {
          dispatch({ type: "fetched", payload: response });
          nextRequest.current = false;
        }
      } catch (error: any) {
        if (unMounted.current || abortSignal?.aborted) return;

        if (!nextRequest.current) {
          dispatch({ type: "error", payload: error as Error });
        }
      }
    };

    void fetchData();

    return () => {
      if (state.loading) {
        nextRequest.current = true;
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  return {
    ...state,
    refetch: () => {
      cache.current = {};

      // TO DO: This fetchData function is repeated from on the second useEffect too, refactor to have it only once.
      const fetchData = async () => {
        dispatch({ type: "loading" });

        if (cache.current[name]) {
          dispatch({ type: "fetched", payload: cache.current[name] });
          return;
        }

        try {
          const { response } = await method(variables, { abort: abortSignal });

          cache.current[name] = response;

          if (unMounted.current) {
            return;
          }

          if (!nextRequest.current) {
            dispatch({ type: "fetched", payload: response });
            nextRequest.current = false;
          }
        } catch (error: any) {
          if (unMounted.current || abortSignal?.aborted) return;

          if (!nextRequest.current) {
            dispatch({ type: "error", payload: error as Error });
          }
        }
      };

      void fetchData();
    },
  };
}

export default useGrpcQuery;
